// src/components/ChunkErrorBoundary.js
//
// Wraps the app so that a failed React.lazy() import (stale chunk hash from
// a browser tab left open across a deploy) triggers a silent one-time
// reload instead of crashing the page to a blank screen. Real rendering
// errors (not chunk-related) fall through to a plain "please refresh"
// message rather than being silently swallowed.

import React from 'react';
import { reloadIfChunkError, isChunkLoadError } from '../utils/chunkErrorHandler';

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, errorInfo) {
    if (reloadIfChunkError(error)) return;
    console.error('App crashed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        // Reload is already in flight (or was already tried this session).
        // Show a lightweight loader rather than nothing.
        return (
          <div className="flex items-center justify-center h-screen bg-gray-100">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-sm">Updating to the latest version...</p>
            </div>
          </div>
        );
      }

      return (
        <div className="flex items-center justify-center h-screen bg-gray-100">
          <div className="text-center max-w-sm px-4">
            <p className="text-gray-800 font-semibold mb-2">Something went wrong.</p>
            <p className="text-gray-600 text-sm mb-4">Please refresh the page to continue.</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm font-medium"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
